import React, { SyntheticEvent, useCallback, useState } from 'react';
import { Field as AkField } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import { TaxRate } from '../../data/taxRates/model';
import { Country as CountryType } from '../../data/countries/model';
import t from '../../i18n/intl';
import { TaxFreeMessage, countryIsoCodeChile } from 'modules/checkout/contacts/TaxIdInput';
import { Radio } from '@atlaskit/radio';
import Help from '../../../components/common/Help';

type TaxIdRadioProps = {
  vatResponsible?: boolean | null;
  onChangeVatResponsible: (value: boolean) => void;
  isTaxIdEmpty: boolean;
};

const TaxIdRadio = ({ vatResponsible, isTaxIdEmpty, onChangeVatResponsible }: TaxIdRadioProps) => {
  const handleRadioChange = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    if (e.currentTarget.value === 'required') {
      onChangeVatResponsible(true);
    } else if (e.currentTarget.value === 'notRequired') {
      onChangeVatResponsible(false);
    }
  }, []);

  if (isTaxIdEmpty) return null;
  return (
    <>
      <Radio
        isChecked={vatResponsible === true}
        name="vatRequired"
        value="required"
        label={
          <>
            {t('checkout.contact.tax-id-vat-required')}
            <Help>{t('checkout.contact.tax-id-vat-required.help')}</Help>
          </>
        }
        onChange={handleRadioChange}
      />
      <Radio
        isChecked={vatResponsible === false}
        name="vatNotRequired"
        value="notRequired"
        label={t('checkout.contact.tax-id-vat-not-required')}
        onChange={handleRadioChange}
      />
    </>
  );
};

type Props = {
  country: CountryType | null;
  taxRate: TaxRate | null;
  taxId?: string | null;
  vatResponsible?: boolean | null;
  onChangeVatResponsible: (value: boolean) => void;
};

export const ContactTaxIdI = ({
  country,
  taxRate,
  taxId,
  vatResponsible,
  onChangeVatResponsible,
}: Props) => {
  const [isTaxIdFieldEmpty, setIsTaxIdFieldEmpty] = useState(!taxId);

  const handleChange = (value) => setIsTaxIdFieldEmpty(!value);

  const showTaxId = taxRate && taxRate?.taxIdLabel !== 'Tax ID';
  const isCountryChile = country?.isoCountryCode === countryIsoCodeChile;
  const label =
    taxRate?.taxIdLabel === 'ABN' ? 'Australian Business Number (ABN)' : taxRate?.taxIdLabel;

  return showTaxId ? (
    <>
      <AkField
        name="taxId"
        testId="taxId"
        label={label}
        defaultValue={taxId}
        validate={handleChange}
      >
        {({ fieldProps }) => (
          <>
            <TextField
              autoComplete="off"
              {...fieldProps}
              placeholder={isCountryChile ? 'XX.XXX.XXX-X' : ''}
            />
          </>
        )}
      </AkField>
      <TaxFreeMessage taxRate={taxRate} country={country} />
      {isCountryChile && (
        <AkField name="vatResponsible">
          {({ fieldProps }) => (
            <TaxIdRadio
              isTaxIdEmpty={isTaxIdFieldEmpty}
              vatResponsible={vatResponsible}
              onChangeVatResponsible={onChangeVatResponsible}
              {...fieldProps}
            />
          )}
        </AkField>
      )}
    </>
  ) : null;
};
